import { useState, useCallback, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  getRoles,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  getRoleStaff,
  assignPermissionToRole,
  selectRoles,
  selectRolesLoading,
  selectRole,
  selectRoleLoading,
  selectRoleStaff
} from "store/staff"

export const useRolesAccess = () => {
  const dispatch = useDispatch()

  const roles = useSelector(selectRoles)
  const rolesLoading = useSelector(selectRolesLoading)
  const selectedRoleDetails = useSelector(selectRole)
  const roleLoading = useSelector(selectRoleLoading)
  const assignedStaffForRole = useSelector(selectRoleStaff)

  const [selectedRoleForAccess, setSelectedRoleForAccess] = useState(null)
  const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false)
  const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState(false)
  const [isDeleteRoleModalOpen, setIsDeleteRoleModalOpen] = useState(false)
  const [editingRole, setEditingRole] = useState(null)
  const [deletingRole, setDeletingRole] = useState(null)

  useEffect(() => {
    dispatch(getRoles())
  }, [dispatch])

  useEffect(() => {
    if (roles.length > 0 && !selectedRoleForAccess) {
      const firstRole = roles[0]
      setSelectedRoleForAccess(firstRole)
      if (firstRole.id) {
        dispatch(getRole(firstRole.id))
      }
    }
  }, [roles, selectedRoleForAccess, dispatch])

  useEffect(() => {
    if (selectedRoleForAccess?.id) {
      dispatch(getRole(selectedRoleForAccess.id))
    }
  }, [selectedRoleForAccess?.id, dispatch])

  const handleAddRole = useCallback(() => {
    setIsAddRoleModalOpen(true)
  }, [])

  const handleRoleEdit = useCallback((role) => {
    setEditingRole(role)
    setIsEditRoleModalOpen(true)
  }, [])

  const handleRoleDelete = useCallback(
    async (role) => {
      setDeletingRole(role)

      if (role.id) {
        dispatch(getRoleStaff(role.id))
      }

      setIsDeleteRoleModalOpen(true)
    },
    [dispatch]
  )

  const handleConfirmDelete = useCallback(
    async (newRoleId) => {
      if (!deletingRole?.id) return

      try {
        await dispatch(
          deleteRole(deletingRole.id, {
            newRoleId: newRoleId
          })
        )
        await dispatch(getRoles())
        setIsDeleteRoleModalOpen(false)
        setDeletingRole(null)
        if (selectedRoleForAccess?.id === deletingRole.id && roles.length > 1) {
          setSelectedRoleForAccess(roles[0].id !== deletingRole.id ? roles[0] : roles[1])
        }
      } catch (error) {
        console.error("Failed to delete role:", error)
      }
    },
    [deletingRole, selectedRoleForAccess, roles, dispatch]
  )

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteRoleModalOpen(false)
    setDeletingRole(null)
  }, [])

  const handleSaveNewRole = useCallback(
    async (roleData) => {
      try {
        const createResult = await dispatch(createRole(roleData))
        const createdRole = createResult?.data

        if (!createdRole?.id) {
          console.error("No role ID returned from API")
          return
        }

        const rolesResult = await dispatch(getRoles())

        await dispatch(getRole(createdRole.id))

        const updatedRoles = rolesResult?.data
        const newRole = updatedRoles?.find((role) => role.id === createdRole.id)

        if (newRole) {
          setSelectedRoleForAccess(newRole)
        } else {
          setSelectedRoleForAccess(createdRole)
        }

        setIsAddRoleModalOpen(false)
      } catch (error) {
        console.error("Failed to create role:", error)
      }
    },
    [dispatch]
  )

  const handleSaveEditRole = useCallback(
    async (roleData) => {
      if (!editingRole?.id) return

      try {
        await dispatch(updateRole(editingRole.id, roleData))

        setEditingRole(null)

        await dispatch(getRoles())
        await dispatch(getRole(editingRole.id))

        setIsEditRoleModalOpen(false)
      } catch (error) {
        console.error("Failed to update role:", error)
      }
    },
    [editingRole, dispatch]
  )

  const handleCloseAddModal = useCallback(() => {
    setIsAddRoleModalOpen(false)
  }, [])

  const handleCloseEditModal = useCallback(() => {
    setIsEditRoleModalOpen(false)
    setEditingRole(null)
  }, [])

  const handlePermissionChange = useCallback(
    async (roleId, permissionData) => {
      try {
        await dispatch(assignPermissionToRole(roleId, permissionData))
      } catch (error) {
        console.error("Failed to update permission:", error)
        throw error
      }
    },
    [dispatch]
  )

  return {
    roles,
    rolesLoading,
    roleLoading,
    assignedStaffForRole,
    selectedRoleForAccess,
    selectedRoleDetails,
    setSelectedRoleForAccess,
    isAddRoleModalOpen,
    isEditRoleModalOpen,
    isDeleteRoleModalOpen,
    editingRole,
    deletingRole,
    handleAddRole,
    handleRoleEdit,
    handleRoleDelete,
    handleConfirmDelete,
    handleCloseDeleteModal,
    handleSaveNewRole,
    handleSaveEditRole,
    handleCloseAddModal,
    handleCloseEditModal,
    handlePermissionChange
  }
}
